import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useWatch } from 'react-hook-form-mui';
import { Button, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    currencyFormatter,
    useAuth,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Order } from '@barboxx/models';
import { OrderStatus } from '@barboxx/types';
import { getBusinessDayQueryRange } from '@barboxx/utils';

import {
    downloadCsv,
    getSummaryExportColumns,
} from '../../../Orders/OrdersList/components/exportUtils';

export interface FilterFormProps {
    startedAt: Date;
    endedAt: Date;
    status: OrderStatus[];
}

const COLUMNS: GridColDef<Order.SummaryModel>[] = [
    {
        field: 'business.place.name',
        headerName: 'Business',
        valueGetter: dataGridNestedObjectValueGetter,
        sortable: false,
        width: 200,
    },
    {
        field: 'orderCount',
        headerName: '# Orders',
        width: 100,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'customerCount',
        headerName: '# Customers',
        width: 150,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'orderItemCount',
        headerName: '# Order Items',
        width: 150,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'drinksRequested',
        headerName: '# Drinks Requested',
        width: 170,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'drinksFulfilled',
        headerName: '# Drinks Fulfilled',
        width: 170,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'subTotal',
        headerName: 'SubTotal',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'serviceFee',
        headerName: 'Service Fees',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'taxAmount',
        headerName: 'Taxes',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'tipAmount',
        headerName: 'Tips',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'netChargedAmount',
        headerName: 'Net Charged',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'grossChargedAmount',
        headerName: 'Gross Charged',
        width: 170,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'refundedAmount',
        headerName: 'Refunded',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'paymentAdjustmentCount',
        headerName: '# Adjustments',
        width: 140,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'applicationFee',
        headerName: 'App Fees',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'expectedPayout',
        headerName: 'Expected Payout',
        width: 180,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'creditCardProcessingFeesCollected',
        headerName: 'Credit Card Fees Collected',
        width: 240,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'stripeFees',
        headerName: 'Stripe Fees',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
    {
        field: 'profit',
        headerName: 'Profit',
        width: 150,
        valueFormatter: currencyFormatter,
        align: 'right',
        headerAlign: 'right',
    },
];

export function OrdersSummary() {
    const {
        auth: { user },
    } = useAuth();
    const { businessId, businessList } = useBusinessSelector();

    const selectedBusiness = React.useMemo(() => {
        if (businessId) {
            return businessList.find(business => business.id === businessId);
        }

        if (user.userType === 'Business' && businessList.length === 1) {
            return businessList[0];
        }

        return undefined;
    }, [businessId, businessList, user.userType]);

    const { status, startedAt, endedAt } = useWatch();
    const { startDateTime, endDateTime } = getBusinessDayQueryRange(
        startedAt,
        endedAt,
        {
            hoursOfOperations: selectedBusiness?.place?.hoursOfOperations,
        }
    );

    const orderSummaryResults = useQuery({
        queryKey: [
            'orderSummary',
            status,
            businessId,
            startDateTime,
            endDateTime,
        ],
        queryFn: () =>
            Api.Orders.Order.getOrdersSummary({
                status,
                businessId,
                startDateTime,
                endDateTime,
            }),
    });

    const FINAL_COLUMNS = React.useMemo(() => {
        return COLUMNS.filter(c => {
            if (user.userType === 'Internal') {
                return true;
            } else {
                return !['applicationFee', 'stripeFees', 'profit'].includes(
                    c.field
                );
            }
        });
    }, [user.userType]);

    const exportColumns = React.useMemo(
        () => getSummaryExportColumns(user.userType),
        [user.userType]
    );

    const handleExportSummary = React.useCallback(() => {
        const rows = orderSummaryResults.data?.data ?? [];
        downloadCsv(
            `sales-summary-${new Date().toISOString()}.csv`,
            rows,
            exportColumns
        );
    }, [orderSummaryResults.data?.data, exportColumns]);

    const HeaderComponent = React.useCallback(
        () => (
            <Stack direction="row" spacing={1}>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportSummary}
                    disabled={
                        orderSummaryResults.isFetching ||
                        !orderSummaryResults.data?.data?.length
                    }
                >
                    Export Summary CSV
                </Button>
            </Stack>
        ),
        [
            handleExportSummary,
            orderSummaryResults.isFetching,
            orderSummaryResults.data?.data?.length,
        ]
    );

    return (
        <DataGridCard title="Sales Summary" HeaderComponent={HeaderComponent}>
            <DataGrid
                columns={FINAL_COLUMNS}
                disableColumnMenu
                disableRowSelectionOnClick
                loading={orderSummaryResults.isFetching}
                rows={orderSummaryResults.data?.data ?? []}
                rowCount={orderSummaryResults.data?.data?.length ?? 0}
                hideFooterPagination
                getRowId={row => `${row.businessId}`}
            />
        </DataGridCard>
    );
}
